// LegalEntitiesTable.tsx
'use client';

import React, { useCallback, useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { InventoryEntityState, LegalEntitySortField, LeasingLegalEntityFilters } from '#types/types';
import { Table, TableColumnDef } from '../../../portal/Table';
import { Activity, Building, Calendar, Gauge, Hash, MapPin, Store, TrendingUp } from '@/components/ui/lucide-memo';

const translations = {
  en: {
    sortAlphabetical: 'Alphabetical',
    sortFinancialScore: 'Financial Score',
    sortRevenueGrowth: 'Revenue Growth',
    sortShareCapital: 'Share Capital',
    sortWorkforce: 'Workforce Size',
    legalNameHeader: 'Legal Name',
    registrationHeader: 'Registration (SIREN)',
    headquartersHeader: 'Headquarters',
    brandsHeader: 'Brands',
    scoreHeader: 'Score',
    statusHeader: 'Status',
    growthHeader: 'Growth',
    foundedHeader: 'Founded',
  },
  fr: {
    sortAlphabetical: 'Alphabetique',
    sortFinancialScore: 'Score financier',
    sortRevenueGrowth: 'Croissance revenus',
    sortShareCapital: 'Capital social',
    sortWorkforce: 'Effectifs',
    legalNameHeader: 'Raison sociale',
    registrationHeader: 'Immatriculation (SIREN)',
    headquartersHeader: 'Siege',
    brandsHeader: 'Marques',
    scoreHeader: 'Score',
    statusHeader: 'Statut',
    growthHeader: 'Croissance',
    foundedHeader: 'Creation',
  },
};

interface LegalEntitiesTableProps {
  lockedFilters?: Partial<LeasingLegalEntityFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  scopedIds?: string[] | null;
}

export const LegalEntitiesTable = React.memo(({
  lockedFilters,
  assetLedgerTitle = 'All legal entities',
  assetLedgerBreadcrumb = 'Inventory / Legal entities',
  scopedIds,
}: LegalEntitiesTableProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  // 1. Définition des colonnes
  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'legalName', icon: Building, minWidth: 200, header: t.legalNameHeader },
    { id: 'registration', icon: Hash, minWidth: 140, header: t.registrationHeader },
    { id: 'headquarters', icon: MapPin, minWidth: 140, header: t.headquartersHeader },
    { id: 'brands', icon: Store, minWidth: 120, header: t.brandsHeader },
    { id: 'score', icon: Gauge, minWidth: 80, header: t.scoreHeader },
    { id: 'status', icon: Activity, minWidth: 100, header: t.statusHeader },
    { id: 'growth', icon: TrendingUp, minWidth: 100, header: t.growthHeader },
    { id: 'founded', icon: Calendar, minWidth: 100, header: t.foundedHeader, align: 'right' },
  ], [t]);

  const sortOptions = useMemo<{ label: string; field: LegalEntitySortField }[]>(() => [
    { label: t.sortAlphabetical, field: 'NAME' },
    { label: t.sortFinancialScore, field: 'SCORE' },
    { label: t.sortRevenueGrowth, field: 'GROWTH' },
    { label: t.sortShareCapital, field: 'CAPITAL' },
    { label: t.sortWorkforce, field: 'WORKFORCE' },
  ], [t]);

  const renderLegalEntityRow = useCallback((
    entity: InventoryEntityState<'LegalEntity'>,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        type="LegalEntity"
        entityId={entity.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
      />
      {isExpanded && (
        <EntityView
          type="LegalEntity"
          entityId={entity.id}
          variant="page"
          pageHasEntityRowAbove
          className="h-auto border-b border-[#2A2A2A]"
        />
      )}
    </div>
  ), []);

  return (
    <Table
      entityType="LegalEntity"
      initialFilters={lockedFilters}
      lockedFilters={lockedFilters}
      scopedIds={scopedIds}
      columns={columns}
      sortOptions={sortOptions}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      renderRow={renderLegalEntityRow}
    />
  );
});

LegalEntitiesTable.displayName = 'LegalEntitiesTable';

